import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Tooltip,
  Menu,
  Avatar,
  MenuItem,
  Divider,
  ListItemIcon,
  Typography,
  Button,
  Chip,
  CircularProgress,
  ListSubheader,
} from '@mui/material';
import { deepOrange } from '@mui/material/colors';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import ApprovalIcon from '@mui/icons-material/Approval';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EventNoteIcon from '@mui/icons-material/EventNote';
import Logout from '@mui/icons-material/Logout';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import useProfileImage from '../../../hooks/useProfileImage';
import * as authApi from '../../../lib/api/auth';
import * as hrAttendApi from '../../../lib/api/hrAttend';

const UserMenu = ({ user, onLogout, compact = false }) => {
  let navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [anchorEl, setAnchorEl] = useState(null);
  const [briefing, setBriefing] = useState(null);
  const [briefingLoading, setBriefingLoading] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const linkToProfile = () => {
    navigate('/profile');
  };

  const linkToDashboard = () => {
    navigate('/dashboard');
  };

  const linkToApproval = () => {
    navigate('/approvalmng/inbox');
  };

  const linkToMyAttend = () => {
    navigate('/attendancemng?tab=attend&scope=me');
  };

  const linkToMyWorkSchedule = () => {
    navigate('/attendancemng?tab=work');
  };

  const linkToMyLeaveRequest = () => {
    navigate('/attendancemng?tab=my&action=request');
  };

  const loadBriefing = useCallback(async () => {
    if (!user?.usrUuid) return;

    setBriefingLoading(true);
    try {
      const res = await authApi.getTodayBootstrap();
      setBriefing(res.data.info);
    } catch (e) {
      setBriefing(null);
    } finally {
      setBriefingLoading(false);
    }
  }, [user?.usrUuid]);

  useEffect(() => {
    if (open) {
      loadBriefing();
    }
  }, [open, loadBriefing]);

  const handleCheckOut = async (event) => {
    event.stopPropagation();
    setCheckingOut(true);
    try {
      await hrAttendApi.checkOutToday(true);
      enqueueSnackbar('퇴근 처리되었습니다.', { variant: 'success' });
      await loadBriefing();
    } catch (e) {
      enqueueSnackbar(
        e?.response?.data?.message ||
          e?.response?.data?.userMessage ||
          '퇴근 처리에 실패했습니다.',
        { variant: 'error' },
      );
    } finally {
      setCheckingOut(false);
    }
  };

  const handleCheckIn = async (event) => {
    event.stopPropagation();
    setCheckingIn(true);
    try {
      await hrAttendApi.checkInToday();
      enqueueSnackbar('출근 처리되었습니다.', { variant: 'success' });
      await loadBriefing();
    } catch (e) {
      enqueueSnackbar(e?.response?.data?.message || '출근 처리에 실패했습니다.', { variant: 'error' });
    } finally {
      setCheckingIn(false);
    }
  };

  // 프로필 이미지 URL (axios를 통해 인증 토큰과 함께 로드)
  const profileImageSrc = useProfileImage(user?.usrUuid, user?.profileImg);
  const attendance = briefing?.attendance || {};
  const checkOutDisabled =
    checkingOut ||
    briefingLoading ||
    !attendance.attendUuid ||
    !!attendance.outTm ||
    false;
  const hasCheckedIn = !!attendance.attendUuid && !!attendance.inTm;

  return (
    <Box>
      <Tooltip title="사용자 메뉴">
        <Button
          color="default"
          variant="text"
          onClick={handleClick}
          sx={{
            borderRadius: '10px',
            minWidth: compact ? 40 : undefined,
            px: compact ? 0.5 : 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            {!compact && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-end',
                  alignContent: 'center',
                }}
              >
                <Typography variant="subtitle2" component="div">
                  {user?.usrNm} {user?.gradeNm}
                </Typography>
                <Typography
                  variant="body2"
                  component="div"
                  color="text.secondary"
                >
                  {user?.dptNm}
                </Typography>
              </Box>
            )}
            <Box sx={{ px: compact ? 0 : 1 }}>
              {profileImageSrc ? (
                <Avatar
                  alt={user?.usrNm}
                  src={profileImageSrc}
                  sx={{ width: 32, height: 32 }}
                />
              ) : (
                <Avatar
                  sx={{ bgcolor: deepOrange[500], width: 32, height: 32 }}
                >
                  {user?.usrNm?.substring(0, 1)}
                </Avatar>
              )}
            </Box>
          </Box>
        </Button>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            width: { xs: 'calc(100vw - 24px)', sm: 'auto' },
            maxWidth: 320,
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        MenuListProps={{
          sx: {
            // 메뉴 항목이 늘어나도 화면(헤더/안전영역 제외)을 넘지 않고
            // 스크롤로 전부 도달 가능하게 한다. 로그아웃은 하단 sticky 푸터로 고정.
            maxHeight: 'calc(100dvh - 72px)',
            overflowY: 'auto',
            pb: 0,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: { xs: 1.5, sm: 2 }, py: { xs: 1, sm: 1.5 }, width: { xs: '100%', sm: 280 } }}>
          <Typography variant="subtitle2" fontWeight={700}>
            {user?.usrNm} {user?.gradeNm}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {user?.dptNm}
          </Typography>

          <Box sx={{ mt: { xs: 1, sm: 1.5 }, p: { xs: 1, sm: 1.25 }, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="body2" fontWeight={700} sx={{ flex: 1 }}>
                오늘 근태
              </Typography>
              {briefingLoading ? (
                <CircularProgress size={16} />
              ) : (
                <Chip
                  size="small"
                  color={attendance.outTm ? 'success' : 'primary'}
                  label={attendance.outTm ? '퇴근 완료' : '근무 중'}
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  출근
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  {attendance.inTm || '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  퇴근
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  {attendance.outTm || '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  기준
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  {attendance.regularOutTm || '18:00'}
                </Typography>
              </Box>
            </Box>
            {!hasCheckedIn ? (
              <Button fullWidth size="small" variant="contained" onClick={handleCheckIn} disabled={checkingIn || briefingLoading} sx={{ mt: { xs: 1, sm: 1.25 } }}>
                {checkingIn ? '출근 처리 중...' : '출근하기'}
              </Button>
            ) : <Button
              fullWidth
              size="small"
              variant="outlined"
              disabled={checkOutDisabled}
              onClick={handleCheckOut}
              sx={{ mt: { xs: 1, sm: 1.25 } }}
            >
              {attendance.outTm
                ? '퇴근 완료'
                : '퇴근하기'}
            </Button>}
          </Box>
        </Box>
        <Divider />

        <MenuItem onClick={() => linkToDashboard()} sx={{ py: { xs: 0.5, sm: 1 } }}>
          <ListItemIcon>
            <DashboardIcon fontSize="small" />
          </ListItemIcon>
          대시보드
        </MenuItem>

        <MenuItem onClick={() => linkToProfile()} sx={{ py: { xs: 0.5, sm: 1 } }}>
          <ListItemIcon>
            <ManageAccountsIcon fontSize="small" />
          </ListItemIcon>
          내 정보
        </MenuItem>

        <ListSubheader
          component="div"
          sx={{
            lineHeight: { xs: '26px', sm: '32px' },
            fontSize: '0.8rem',
            fontWeight: 700,
            color: 'text.secondary',
            bgcolor: 'background.paper',
          }}
        >
          근태 관리
        </ListSubheader>

        <MenuItem onClick={() => linkToMyAttend()} sx={{ pl: 3, py: { xs: 0.5, sm: 1 } }}>
          <ListItemIcon>
            <AccessTimeIcon fontSize="small" />
          </ListItemIcon>
          내 근태 조회
        </MenuItem>

        <MenuItem onClick={() => linkToMyWorkSchedule()} sx={{ pl: 3, py: { xs: 0.5, sm: 1 } }}>
          <ListItemIcon>
            <EventNoteIcon fontSize="small" />
          </ListItemIcon>
          내 근무표
        </MenuItem>

        <MenuItem onClick={() => linkToMyLeaveRequest()} sx={{ pl: 3, py: { xs: 0.5, sm: 1 } }}>
          <ListItemIcon>
            <EventAvailableIcon fontSize="small" />
          </ListItemIcon>
          휴가/근태 신청
        </MenuItem>
        <Divider />

        <MenuItem onClick={() => linkToApproval()} sx={{ py: { xs: 0.5, sm: 1 } }}>
          <ListItemIcon>
            <ApprovalIcon fontSize="small" />
          </ListItemIcon>
          결재함
        </MenuItem>

        {/* 하단 sticky 푸터: 로그아웃은 항상 스크롤 없이 도달 가능해야 하므로 고정.
            오픈소스 라이선스는 법적 고지일 뿐 주요 동작이 아니므로 작은 보조 링크로 축소 (2026-10-28). */}
        <Box
          component="li"
          role="presentation"
          sx={{
            position: 'sticky',
            bottom: 0,
            zIndex: 2,
            mt: 0.5,
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: { xs: 0.75, sm: 1 },
          }}
        >
          <Typography
            component="span"
            role="link"
            tabIndex={0}
            variant="caption"
            color="text.secondary"
            onClick={() =>
              window.open('/third-party-notices.txt', '_blank', 'noopener,noreferrer')
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                window.open('/third-party-notices.txt', '_blank', 'noopener,noreferrer');
              }
            }}
            sx={{
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 14 }} />
            오픈소스 라이선스
          </Typography>

          <Box
            role="menuitem"
            tabIndex={0}
            onClick={() => onLogout()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onLogout();
              }
            }}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              cursor: 'pointer',
              color: 'text.primary',
              fontSize: '0.875rem',
              fontWeight: 500,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <Logout fontSize="small" />
            로그아웃
          </Box>
        </Box>
      </Menu>
    </Box>
  );
};

export default UserMenu;
